import React, { lazy, Suspense, useEffect, useState } from "react";
import DefaultModal from "../DefaultModal";
import { useTranslation } from "react-i18next";
import ShowContainer from "../../mui/ShowContainer";
import { API_USER } from "../../../api/user";
import { useAppSelector } from "../../../store/index.ts";
import { ENUM_DEPOSIT_STATUS, TEST_MODE } from "../../../store/enums";
import { formatDateTime } from "../../../utils/utils.js";
import useDecimalValue from "../../../hooks/useDecimalValue.js";
import DetailsPayouts from "./details/DetailsPayouts.jsx";
import "./my-payouts.scss";

const MyTable = lazy(() => import("../../mui/my_table/MyTable.jsx"));

const test = [
  {
    commission: 1,
    dateTime: "2025-12-11 09:04:33",
    invoiceAmount: 10,
    invoiceId: 128,
    payoutAmount: 9,
    status: 8
  },
  {
    commission: 3,
    dateTime: "2025-12-11 09:04:33",
    invoiceAmount: 10,
    invoiceId: 129,
    payoutAmount: 9,
    status: 8
  },
  {
    commission: 1,
    dateTime: "2025-12-11",
    invoiceAmount: 10,
    invoiceId: 122,
    payoutAmount: 9,
    status: 8
  }
];

export default function MyPayoutsModal({ isOpen, closeModal }) {
  const { t } = useTranslation();
  const getDecimalValue = useDecimalValue();

  const currency = useAppSelector((state) => state.user.currency);

  const [payoutsDays, setPayoutsDays] = useState(0);
  const [payouts, setPayouts] = useState([]);
  const [statusHash, setStatusHash] = useState([]);
  const [openInvoiceId, setOpenInvoiceId] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    if (!currency) return;

    if (TEST_MODE) {
      setPayouts(test);
      setStatusHash({
        0: "any",
        1: "created",
        3: "canceled",
        6: "paidout",
        7: "inprocess",
        8: "refunded",
        9: "failed",
        10: "unknown"
      });
      return;
    }

    API_USER.balance
      .payoutList({ currency })
      .then((e) => {
        const data = e.data.data;
        setStatusHash(data.statusList);
        setPayouts(data.payoutList);
        setPayoutsDays(data.options.payoutLastDays);
      })
      .catch(console.log);
  }, [currency, isOpen]);

  const headers = [
    { accessor: "id", label: t("Invoice ID"), width: 80, type: "number" },
    {
      accessor: "withdrawal",
      label: t("Withdrawal amount"),
      width: "auto",
      type: "string"
    },
    {
      accessor: "commission",
      label: t("Commission"),
      width: "auto",
      type: "string"
    },
    {
      accessor: "received",
      label: t("Amount to be received"),
      width: "auto",
      type: "string"
    },
    {
      accessor: "status",
      align: "center",
      label: t("Status"),
      width: 110,
      type: "string"
    },
    {
      accessor: "dateTime",
      label: t("Date"),
      width: 140,
      type: "string"
    }
  ];

  const rows = payouts.map((el) => ({
    id: (
      <div className="copy-invoice pointer" onClick={() => setOpenInvoiceId(el.invoiceId)}>
        {el.invoiceId}
      </div>
    ),
    withdrawal: `${getDecimalValue(el.invoiceAmount, currency)} ${currency}`,
    commission: `${getDecimalValue(el.commission, currency)} ${currency}`,
    received: `${getDecimalValue(el.payoutAmount, currency)} ${currency}`,
    status: t(ENUM_DEPOSIT_STATUS[statusHash[el.status]]),
    dateTime: formatDateTime(el.dateTime)
  }));

  return (
    <>
      <DefaultModal
        largeModal
        isOpen={isOpen}
        closeModal={closeModal}
        classModal="my-deposit-modal my-payout-modal">
        <h2>{t("Withdrawal transactions")}</h2>

        <ShowContainer condition={payoutsDays}>
          <p>{t("Displays all withdrawal requests for the last days.", { payoutsDays })}</p>
        </ShowContainer>

        <div className="table-scroller">
          {isOpen && (
            <Suspense>
              <MyTable rows={rows} headers={headers} />
            </Suspense>
          )}
        </div>
      </DefaultModal>

      <DetailsPayouts
        isOpen={!!openInvoiceId}
        invoiceId={openInvoiceId}
        closeModal={() => setOpenInvoiceId(false)}
      />
    </>
  );
}
